// Marker lives in its own dependency-free module, NOT here, even though this is the code
// that writes it. `index.tsx` needs it for `beforeSend`, and importing this component from
// the entry module reorders the app's module-graph initialisation — see the warning in
// `errorBoundaryMarker.ts`.
import { ERROR_BOUNDARY_CONSOLE_MARKER } from '@lib/utils/errorBoundaryMarker';
import { showToast } from '@lib/utils/toastmaster';
import { ActionButton } from '@pages/ActionButton';
import * as Sentry from '@sentry/react';
import { trackingStore } from '@stores/Tracking';
import { Component, Fragment, ReactNode } from 'react';
import css from './SubtreeErrorBoundary.module.scss';

interface Props {
  /**
   * Identifies this boundary in Sentry (tag `boundary`). Use the name of the
   * subtree being guarded, not the page — the whole point is to know WHICH part
   * broke, so `"LibraryContent"` and `"MomentModal"` must not share a boundary.
   */
  name: string;
  children: ReactNode;
  /**
   * Replaces the default fallback panel entirely. Pass `null` to render nothing —
   * correct for overlays, where a panel would sit behind the page it replaced.
   * Omitting the prop is what selects the default panel, so `null` and "not given"
   * mean different things here.
   */
  fallback?: ReactNode;
  /**
   * Extra Sentry context, evaluated lazily at catch time so it reflects the state
   * that actually broke rather than the state at mount.
   */
  getExtra?: () => Record<string, unknown>;
  /**
   * Values identifying WHAT the guarded subtree is currently showing. When any of them
   * changes, the error clears and the subtree is remounted.
   *
   * This is not optional polish. React reconciles by position and type, so a boundary
   * inside a route that only changes its params — `/library/:id`, or
   * `/library/:id/media/:mediaId` — is the SAME instance before and after navigation,
   * and `hasError` outlives the thing that broke. Without this, a crash on library A
   * shows the fallback for library B, and a crash in a `fallback={null}` overlay
   * disables that overlay for the rest of the page's life with nothing on screen to
   * explain why.
   */
  resetKeys?: unknown[];
}

/**
 * Shallow, order-sensitive comparison of the two `resetKeys` arrays.
 *
 * Compares CONTENTS, not identity: `resetKeys={[id]}` allocates a fresh array every
 * render, so an identity check would report a change constantly and remount the subtree
 * forever.
 *
 * Only "never passed at all" means never reset. An absent array on one side is treated as
 * empty rather than as "skip", so a caller writing
 * `resetKeys={cond ? [x] : undefined}` still resets when `cond` flips — bailing out on
 * either-side-undefined would silently disable the reset for exactly that caller, which is
 * the failure this whole mechanism exists to prevent.
 */
function resetKeysChanged(prev: unknown[] | undefined, next: unknown[] | undefined): boolean {
  if (!prev && !next) return false;
  const before = prev ?? [];
  const after = next ?? [];
  if (before.length !== after.length) return true;
  return before.some((value, i) => !Object.is(value, after[i]));
}

/** See the call site: `getExtra` must never be the reason a boundary fails to contain. */
function safeGetExtra(getExtra: (() => Record<string, unknown>) | undefined) {
  if (!getExtra) return undefined;
  try {
    return getExtra();
  } catch (extraError) {
    return { getExtraFailed: String(extraError) };
  }
}

interface State {
  hasError: boolean;
  /** Bumped by "Try again" and used as a `key`, so retrying REMOUNTS the subtree. */
  resetKey: number;
}

/**
 * Catches a render/commit crash in one part of a page and replaces just that part,
 * leaving the rest of the page usable.
 *
 * This is not `ErrorCatcher` (`./ErrorBoundary.tsx`). That one wraps the whole app,
 * shows Sentry's feedback dialog and reloads on dismiss — the "everything died"
 * path. This one is for a subtree you can afford to lose.
 *
 * Scope each instance to a subtree the user can lose independently. A boundary that
 * spans two unrelated features reports the wrong one and takes down the innocent
 * one with it — which is exactly what happened before this existed: a single
 * boundary named for the moment modal wrapped the entire library page, so a crash
 * in the library header blamed the modal and blanked the grid.
 */
export class SubtreeErrorBoundary extends Component<Props, State> {
  /**
   * Whether the pending render is a USER retry, so a second crash can be reported as
   * `afterRetry` and the in-place retry's success rate is measurable.
   *
   * Only "Try again" sets this — a `resetKeys` reset clears it, because navigating to a
   * different library is a fresh attempt at a different thing, not a retry of the one
   * that failed. Conflating them would silently inflate the failure rate.
   */
  private retriedByUser = false;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, resetKey: 0 };
  }

  static getDerivedStateFromError(): Partial<State> {
    return { hasError: true };
  }

  override componentDidUpdate(prevProps: Props) {
    if (!this.state.hasError) {
      // Children committed without throwing, so any retry in flight succeeded and the flag
      // must not leak into the NEXT, unrelated crash — that would report `afterRetry: true`
      // for a first failure and inflate the very failure rate the metric is meant to track.
      //
      // Safe here specifically because a failed retry never reaches this branch: the render
      // that throws is discarded rather than committed, so `componentDidUpdate` only sees
      // `hasError === false` once children have actually survived a commit.
      this.retriedByUser = false;
      return;
    }
    if (resetKeysChanged(prevProps.resetKeys, this.props.resetKeys)) {
      this.retriedByUser = false;
      // Remount rather than merely un-hide: the previous attempt's fibers are the ones
      // that threw, so clearing `hasError` alone would re-render them unchanged.
      this.setState((prev) => ({ hasError: false, resetKey: prev.resetKey + 1 }));
    }
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const { name, getExtra } = this.props;

    // Uses the shared marker so `beforeSend` can drop the duplicate event
    // `captureConsoleIntegration` builds from this line — the console twin's stack is
    // pre-stringified and can never be symbolicated, unlike the capture below.
    console.error(`[${name}] ${ERROR_BOUNDARY_CONSOLE_MARKER}`, error, errorInfo);

    Sentry.captureException(error, {
      // A DISTINCT FINGERPRINT IS LOAD-BEARING, not a grouping preference.
      //
      // `dedupeIntegration()` is on by default and its `processEvent` runs before
      // `beforeSend`. React 19 logs every boundary-caught error itself
      // (`defaultOnCaughtError` → `console.error(error)`), and it does so BEFORE
      // `componentDidCatch` — so `captureConsoleIntegration` turns that into an event
      // wrapping this same Error, which becomes Dedupe's `previousEvent`. Same `type`,
      // `value` and stack means this capture would then be dropped as a duplicate, taking
      // the `boundary` tag, the component stack and `getExtra()` with it and leaving only
      // the context-free console-derived event.
      //
      // Dedupe bails out when fingerprints differ (`_isSameFingerprint`), so naming this
      // one keeps it. `'{{ default }}'` preserves normal per-error grouping underneath, and
      // including `name` means two boundaries' failures don't merge into one issue.
      // `index.tsx` also suppresses React's own log, but this must not depend on that
      // holding — or on `console.error` below staying after this call.
      fingerprint: ['subtree-error-boundary', name, '{{ default }}'],
      tags: {
        boundary: name,
        errorBoundary: true,
        // No `translated` tag here on purpose: `beforeSend` in `index.tsx` attaches that
        // (and the `<font>` count) to EVERY event, so a boundary-local copy would only
        // be a second thing to keep in sync.
      },
      extra: {
        // React's own component stack. Worth more than the JS stack for a
        // commit-phase crash, where every JS frame is a react-dom internal.
        // Note it is NOT source-mapped by Sentry, so expect minified names.
        componentStack: errorInfo.componentStack,
        // Guarded because `getExtra` reads live store state, which is exactly what may
        // be in a broken state right now. An unguarded throw here would escape
        // `componentDidCatch` and escalate to the app-level `ErrorCatcher` — losing the
        // report AND replacing a contained crash with a full-page one.
        ...safeGetExtra(getExtra),
      },
    });

    // Product-side counterpart to the Sentry report: how often users land on a broken
    // section, and whether pressing "Try again" actually recovers it.
    trackingStore.sectionRenderFailed({ boundary: name, afterRetry: this.retriedByUser });

    // No loop guard needed: `getDerivedStateFromError` unmounts the children, so this
    // fires once per crash rather than once per render. Repeats come only from "Try
    // again" or from both boundaries catching in one commit, and the template's stable
    // `id` collapses those into a single toast.
    showToast('section-render-failed');
  }

  private handleRetry = () => {
    trackingStore.sectionRetryClicked({ boundary: this.props.name });
    this.retriedByUser = true;
    this.setState((prev) => ({ hasError: false, resetKey: prev.resetKey + 1 }));
  };

  override render() {
    if (this.state.hasError) {
      // `!== undefined`, not truthiness: `fallback={null}` means "render nothing",
      // which is falsy and would otherwise fall through to the default panel.
      if (this.props.fallback !== undefined) return this.props.fallback;

      return (
        <div className={css.fallback}>
          <div className={css.text}>
            <h1>Something went wrong</h1>
            <span>
              This part of the page couldn&apos;t be displayed. Reloading usually fixes it.
            </span>
          </div>
          <ActionButton variant="primary" onClick={this.handleRetry}>
            Try again
          </ActionButton>
        </div>
      );
    }

    // Keyed so "Try again" gives the subtree a genuinely fresh mount. Without this,
    // clearing `hasError` re-renders the same fibers with the same state and
    // whatever threw simply throws again.
    return <Fragment key={this.state.resetKey}>{this.props.children}</Fragment>;
  }
}
